import * as React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { cn } from '../../lib/utils';
import { EditorToolbar } from './EditorToolbar';
import { PreservedHtml } from './PreservedHtml';
import './RichTextEditor.css';

export interface IRichTextEditorProps {
  label: string;
  value: string;
  initialValue?: string;
  disabled?: boolean;
  error?: string;
  onChange: (_value: string) => void;
  /** Called when the editor loses focus (used by the Formik wrapper to set touched). */
  onBlur?: () => void;
  height: number;
  id?: string;
  /**
   * Left side of the footer bar — what the field keeps. Sits inside the
   * field's own border, per the spike, rather than floating underneath it as
   * loose help text.
   */
  footNote?: React.ReactNode;
}

/** Words, from the HTML the editor holds. Tags out, entities in, then split. */
const countWords = (html: string): number => {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&[a-z]+;/gi, '')
    .trim();
  return text ? text.split(/\s+/).length : 0;
};

/**
 * The product description editor.
 *
 * Built on TipTap (ProseMirror underneath), which replaced TinyMCE 5 — an
 * end-of-life component that we could only ever style from the outside, and
 * that fought the form it lived in:
 *
 * - It re-serialised whatever it was handed the moment it mounted (an em dash
 *   came back as `&mdash;`, block tags came back with newlines between them),
 *   so a product editor reported unsaved changes before anyone had touched it.
 *   That needed a whole HTML-canonicalising comparison to suppress. Here,
 *   opening a document produces no transaction, so it produces no change —
 *   the comparison is gone because the cause is.
 * - Its toolbar was icon buttons inside its own skin, in its own iframe, so
 *   the design had to be approximated from outside and none of it could be
 *   read by the fidelity check. The toolbar is now ordinary buttons in the
 *   page, drawn from the design.
 *
 * What it will not do is quietly rewrite a description nobody edited: content
 * only leaves this component when an edit produces it, so a product opened and
 * closed goes back exactly as it arrived, whatever markup it contains.
 */
export const RichTextEditor: React.FC<IRichTextEditorProps> = ({
  label,
  value,
  initialValue = '',
  disabled,
  error,
  onChange,
  onBlur,
  height,
  id = 'editor',
  footNote,
}) => {
  const [focused, setFocused] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Every heading level, though the toolbar only offers one. The schema
        // is what a document is allowed to contain and the toolbar is what a
        // seller can make: restricting the schema to h3 would have quietly
        // flattened an imported listing's h2 into a paragraph on first save.
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        // Clicking a link inside the editor should put the cursor in it, not
        // navigate away from the half-written product.
        link: { openOnClick: false },
      }),
      PreservedHtml,
    ],
    content: value || initialValue || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'rte-content',
        'data-testid': 'rte-content',
        'aria-label': label,
      },
    },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  React.useEffect(() => {
    // `false` is the second argument for a reason: setEditable announces an
    // update by default, and locking or unlocking a field is not an edit to
    // the document in it. Left at the default it marked the product dirty the
    // moment the editor mounted — the same "unsaved changes on a page that has
    // just loaded" the old editor caused, arrived at a different way.
    editor?.setEditable(!disabled, false);
  }, [disabled, editor]);

  // Sync from outside — a product finishing loading, or a form being reset.
  // Typing does not land here: the value the form holds came from `getHTML`,
  // so it already equals what the editor holds and the guard returns.
  React.useEffect(() => {
    if (!editor) return;
    const next = value || '';
    if (next === editor.getHTML()) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [value, editor]);

  // `value` is empty until the first edit, so a field loaded with content
  // counted zero words until someone typed into it.
  const words = React.useMemo(() => countWords(value || initialValue || ''), [value, initialValue]);

  return (
    <div role="group" aria-label={label} id={id}>
      <div
        ref={wrapRef}
        // The whole field takes the focus ring, as every other input does —
        // the toolbar, the text and the link popover are one control, so
        // moving between them is not leaving the field.
        onFocus={() => setFocused(true)}
        onBlur={(event) => {
          if (wrapRef.current?.contains(event.relatedTarget as Node | null)) return;
          setFocused(false);
          onBlur?.();
        }}
        className={cn(
          'overflow-hidden rounded-md border bg-card transition-shadow',
          'border-border',
          focused && 'border-ring shadow-[0_0_0_1px_hsl(var(--ring))]',
          error && 'border-destructive/50',
          error && focused && 'border-destructive shadow-[0_0_0_1px_hsl(var(--destructive))]',
          disabled && 'bg-muted',
        )}
      >
        {editor && <EditorToolbar editor={editor} disabled={disabled} />}

        <div
          className={cn('overflow-y-auto px-4 py-3', disabled && 'bg-muted')}
          style={{ minHeight: height }}
        >
          <EditorContent editor={editor} />
        </div>

        {/* The footer bar the spike draws: what the field keeps on the left,
            how long the description is on the right. */}
        <div className="flex items-center gap-3 border-t border-border bg-muted px-3 py-1.5">
          {footNote && <span className="text-xs text-muted-foreground">{footNote}</span>}
          <span className="ml-auto text-xs tabular-nums text-muted-foreground">
            {words === 1 ? '1 word' : `${words} words`}
          </span>
        </div>
      </div>
    </div>
  );
};
